import type { GlassMaterial, GlassVariant } from '@typings/glass';

/**
 * The glass materials.
 *
 * Refraction is deliberately strongest on the thinnest glass. Floating
 * controls sit over moving content, which is where bending reads as glass;
 * text panels need a calmer backdrop, so they trade refraction for blur.
 *
 * Blur on `floating` and `regular` is at its floor for contrast: the header
 * sits over the hero video, and the work cards over bright artwork; any
 * less lets the brightest spots pull text below WCAG AA. Refraction does
 * not affect contrast, so it carries the extra glassiness.
 *
 * This is the single source for both render paths: useGlass feeds the same
 * numbers to the SVG filter in liquid mode and, as a custom property, to
 * the plain CSS backdrop-filter in frost mode.
 */
export const GLASS_MATERIALS: Readonly<Record<GlassVariant, GlassMaterial>> = {
  clear: { blur: 2, saturation: 1.8, bezel: 16, refraction: 18 },
  floating: { blur: 24, saturation: 2, bezel: 20, refraction: 22 },
  regular: { blur: 14, saturation: 1.6, bezel: 22, refraction: 16 },
  thick: { blur: 26, saturation: 1.4, bezel: 26, refraction: 10 },
};

/**
 * Displacement maps are cached by geometry so identical cards in a grid
 * share one image. Bounded because a resize drag produces a new size every
 * frame, and each map is a data URL of a few kilobytes.
 */
export const DISPLACEMENT_CACHE_SIZE = 48;

/**
 * Below this many logical cores, liquid glass falls back to frost. Each
 * refracting surface is a separate full-resolution backdrop pass, and on a
 * dual- or quad-core laptop that is the difference between smooth and not.
 */
export const LIQUID_MIN_CORES = 6;
